import { useAuth } from '../contexts/AuthContext';
import { track } from './track';
import axios, {
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig
} from 'axios';
import { useEffect } from 'react';

// Extend AxiosRequestConfig to include _requiresAuth
interface CustomAxiosRequestConfig<
  D = any
> extends InternalAxiosRequestConfig<D> {
  _requiresAuth?: boolean;
  _retry?: boolean;
}

export type ApiError = Error & {
  status?: number;
  data?: any;
};

// Module-level so it is shared across every useNetwork() instance/component.
// When several requests 401 at once (common when the access token expires) they
// all await this single refresh instead of each POSTing /auth/refresh-token —
// which matters now that refresh rotates the httpOnly cookies server-side.
let refreshPromise: Promise<void> | null = null;

export const useNetwork = () => {
  const { logout } = useAuth();
  const apiUrl = process.env.REACT_APP_API_URL;

  // Create an Axios instance. withCredentials sends the httpOnly auth cookies
  // (accessToken + refreshToken) on every request; the server reads the access
  // token from the cookie, so there is no Authorization header to attach here.
  // withXSRFToken echoes the non-httpOnly XSRF-TOKEN cookie in an X-XSRF-TOKEN
  // header (axios 1.6+ requires opting in for cross-origin) — the CSRF double-submit.
  // Scoped to this instance (baseURL is our API) so the token never leaks to the
  // third-party APIs the app also calls.
  const api = axios.create({
    baseURL: apiUrl,
    withCredentials: true,
    withXSRFToken: true
  });

  const refreshAccessToken = async () => {
    try {
      // Coalesce concurrent refreshes into a single in-flight request. The server
      // rotates the httpOnly access + refresh cookies; nothing comes back to JS.
      if (!refreshPromise) {
        refreshPromise = api
          .post('/auth/refresh-token')
          .then(() => undefined)
          .finally(() => {
            refreshPromise = null;
          });
      }
      await refreshPromise;
    } catch (error) {
      console.error('Error refreshing access token', error);
      logout();
      throw error;
    }
  };

  useEffect(() => {
    const responseInterceptor = api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config as CustomAxiosRequestConfig;

        if (
          error.response?.status === 401 &&
          !originalRequest._retry &&
          originalRequest._requiresAuth
        ) {
          originalRequest._retry = true;

          try {
            await refreshAccessToken();
            // Auth rides the refreshed httpOnly cookie — just replay the request.
            return api(originalRequest);
          } catch (refreshError) {
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );

    return () => {
      api.interceptors.response.eject(responseInterceptor);
    };
  }, [refreshAccessToken]);

  const request = async (
    endpoint: string,
    method: AxiosRequestConfig['method'],
    body?: any,
    auth: boolean = false,
    customHeaders: Record<string, string> = {}
  ) => {
    try {
      const headers: Record<string, string> = {
        ...customHeaders
      };

      // Auth rides the httpOnly cookie (withCredentials); no header to attach.

      const response = await api({
        url: endpoint,
        method,
        data: body,
        headers,
        _requiresAuth: auth
      } as CustomAxiosRequestConfig);
      return response.data;
    } catch (error: any) {
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        const data = error.response?.data;

        const serverMsg =
          data?.error?.message ??
          data?.message ??
          (typeof data === 'string' ? data : null) ??
          error.message;

        const err = new Error(
          typeof serverMsg === 'string' ? serverMsg : 'Request failed'
        ) as ApiError;

        err.status = status;
        err.data = data;

        // Friction signal: a user-triggered request failed. Transient session-expiry
        // 401s never reach here (the interceptor refreshes + retries them), so we keep
        // 401s — a failed LOGIN is one and matters. Only skip telemetry itself and the
        // refresh endpoint (its failure is a forced logout, tracked elsewhere).
        if (endpoint !== '/events' && endpoint !== '/auth/refresh-token') {
          track(
            'api_error',
            {
              method: String(method || '').toUpperCase(),
              path: endpoint,
              status: status ?? 0,
              code: (error as any).code
            },
            { category: 'friction' }
          );
        }

        throw err;
      }

      throw error;
    }
  };

  const get = (
    endpoint: string,
    auth: boolean = false,
    customHeaders?: Record<string, string>
  ) => request(endpoint, 'GET', undefined, auth, customHeaders);
  const post = (
    endpoint: string,
    body: any,
    auth: boolean = false,
    customHeaders?: Record<string, string>
  ) => request(endpoint, 'POST', body, auth, customHeaders);
  const put = (
    endpoint: string,
    body: any,
    auth: boolean = false,
    customHeaders?: Record<string, string>
  ) => request(endpoint, 'PUT', body, auth, customHeaders);
  const del = (
    endpoint: string,
    auth: boolean = false,
    customHeaders?: Record<string, string>
  ) => request(endpoint, 'DELETE', undefined, auth, customHeaders);
  const getFile = async (
    endpoint: string,
    auth: boolean = false,
    customHeaders: Record<string, string> = {}
  ) => {
    try {
      const headers: Record<string, string> = {
        ...customHeaders
      };

      // Auth rides the httpOnly cookie (withCredentials); no header to attach.

      const response = await api({
        url: endpoint,
        method: 'GET',
        headers,
        responseType: 'blob',
        _requiresAuth: auth
      } as CustomAxiosRequestConfig);

      return response;
    } catch (error: any) {
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        const data = error.response?.data;

        const err = new Error('File download failed') as ApiError;
        err.status = status;
        err.data = data;
        throw err;
      }

      throw error;
    }
  };

  return { get, getFile, post, put, del };
};
